/**
 * Inner layer clip outline builder（happa v1.1 Phase G、Option A 確定版）。
 *
 * DETONATE 中（progress > 0）に Inner レイヤーの `ctx.clip()` 用輪郭を返す
 * pure 関数。現行実装は **warped silhouette をそのまま 1 要素配列で返す**
 * だけの薄いラッパー。
 *
 * 背景:
 * Phase G の初版（2026-04 Option C）は各破片の displacedPolygon を
 * polygon-clipping で Boolean union し Inner clip としていたが、破片が
 * 互いに離れる progress 中盤以降で disjoint な複数 polygon に分裂し、
 * Inner が各破片の真下だけにしか描画されない症状（破片で覆われて視認
 * できない）を招いた。これは Boolean union の幾何的性質で、バグではなく
 * 設計選択の誤り。
 *
 * Phase G Option A（本実装）は既に `buildSilhouette` が返す warped
 * silhouette が「破片群を包む envelope」として設計されている
 * （`warpPolygonByCells` が各頂点を影響圏内最大変位で押し出す、
 * spec §3.6）点を活かし、この silhouette をそのまま Inner clip として
 * 使う。これにより:
 *   - 常に single connected polygon
 *   - progress で連続的に膨張する envelope
 *   - 破片群の隙間も内包するため Inner 色 / PNG が「広く繋がった面」
 *     として露出する
 *
 * 関数として残す意義:
 * 1 要素配列への包装はレイヤー層（`drawInnerLayer` が `Vec2[][]` を受け取る）
 * との型契約を吸収するため。呼び出し側の分岐ロジック（単一 polygon を
 * 毎回 `[silhouette]` に変換する）を集約して、将来 outline を複数化する
 * 拡張（例: silhouette + hull の足し合わせ）が入った際にここを触れば済む。
 *
 * 全関数 pure。DOM / Canvas / RNG 参照なし。同一入力なら同一出力。
 *
 * 設計参照:
 * - `docs/happa-designtool.md` Phase G（Inner expansion via warped silhouette）
 * - `src/core/silhouette.ts#buildSilhouette`（warped silhouette の出力元）
 * - `src/core/polygon.ts#warpPolygonByCells`（envelope 膨張ロジック）
 */

import type { Vec2 } from './types';

/**
 * 退化 polygon 判定の最小頂点数。
 *
 * 2 頂点以下の silhouette は面積ゼロで clip 対象として不正。呼び出し側で
 * Inner 描画 skip させるため空配列を返す。
 */
const MIN_RING_VERTICES = 3;

/**
 * Inner レイヤーの clip 輪郭を返す（Phase G Option A）。
 *
 * **挙動**: `silhouette` が 3 頂点以上あればシャローコピーして 1 要素配列で
 * 返す。退化 silhouette（頂点 < 3）なら空配列を返し、呼び出し側で Inner
 * 描画を skip させる。
 *
 * **progress 引数の扱い**: 現行実装では参照しない（silhouette 自体が warp
 * 済みで progress 依存の envelope になっている）。API 互換性と将来拡張
 * （progress に応じて outline を合成するバリエーション）のため受け取る
 * だけにする。
 *
 * **pure 性**: DOM / Canvas / RNG / 状態参照なし。シャローコピーで呼び出し
 * 側の mutation から保護。
 *
 * @param silhouette 現フレームの warped silhouette（`buildSilhouette` 結果）。
 *                   Idle（progress=0）でも Explosion（>0）でも同じ引数名で
 *                   渡せる（warp 倍率 0 のとき innerPoly と一致）。
 * @param _progress アニメ進行度 0..1。現行は未使用だが API 予約。
 * @returns clip 用 polygon 配列。silhouette が有効なら `[silhouette.slice()]`、
 *          退化なら `[]`。
 */
export function computeInnerOutline(silhouette: Vec2[], _progress: number): Vec2[][] {
  if (silhouette.length < MIN_RING_VERTICES) return [];
  return [silhouette.slice()];
}
